import { Badge, Dropdown } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import vconst from 'utils/helpers/structs/vconst';
import {
    getObjectPayStatusVariant,
    isObjectPaymentsCovered,
} from '../helpers/objectPayCoverage';

const PAY_STATUSES = [
    vconst.objectPayUnpaid,
    vconst.objectPayPartial,
    vconst.objectPayPaid,
] as const;

interface ObjectPayStatusProps {
    objectId: number;
    payStatus?: number | null;
    objectPrice?: number | null;
    objectPriceRate?: number | null;
    paidUsd?: number | null;
    paidUah?: number | null;
    rate?: number | null;
    onChange?: (payStatus: number) => void | Promise<void>;
    disabled?: boolean;
    /** When false — badge only, no manual change */
    editable?: boolean;
    /** Covered shortcut badge above status (table); hide in analytics card */
    showCoveredHint?: boolean;
}

const ObjectPayStatus = ({
    objectId,
    payStatus = 0,
    objectPrice,
    objectPriceRate,
    paidUsd,
    paidUah,
    rate,
    onChange,
    disabled = false,
    editable = false,
    showCoveredHint = true,
}: ObjectPayStatusProps) => {
    const { t } = useTranslation(['Objects']);

    const coverageParams = { objectPrice, objectPriceRate, paidUsd, paidUah, rate };
    const covered = isObjectPaymentsCovered(coverageParams);

    const storedStatus = Number(payStatus || 0);
    const variant = getObjectPayStatusVariant(storedStatus);
    const canEdit = editable && typeof onChange === 'function';
    const showCoveredBadge =
        showCoveredHint &&
        canEdit &&
        covered &&
        storedStatus !== vconst.objectPayPaid;

    const statusBadge = (
        <Badge bg={variant} className="fs-12">
            {t(`payStatus.${storedStatus}`)}
        </Badge>
    );

    if (!canEdit) {
        return (
            <div className="d-inline-flex flex-column align-items-center gap-1">
                {statusBadge}
            </div>
        );
    }

    const menu = (
        <Dropdown.Menu
            className="object-pay-status-menu shadow-sm"
            popperConfig={{ strategy: 'fixed' }}
            renderOnMount
        >
            {PAY_STATUSES.map((status) => (
                <Dropdown.Item
                    key={`${objectId}-${status}`}
                    className="object-pay-status-menu__item"
                    active={storedStatus === status}
                    onClick={() => {
                        if (storedStatus === status) return;
                        void onChange?.(status);
                    }}
                >
                    <Badge className="fs-12" bg={getObjectPayStatusVariant(status)}>
                        {t(`payStatus.${status}`)}
                    </Badge>
                </Dropdown.Item>
            ))}
        </Dropdown.Menu>
    );

    return (
        <div className="d-inline-flex flex-column align-items-center gap-1">
            {showCoveredBadge ? (
                <button
                    type="button"
                    className="btn p-0 border-0 bg-transparent"
                    disabled={disabled}
                    title={t('payStatusCoveredHint')}
                    onClick={() => void onChange?.(vconst.objectPayPaid)}
                >
                    <Badge bg="" className="fs-12 object-pay-status-covered">
                        {t('payStatus.2')}
                    </Badge>
                </button>
            ) : null}
            <Dropdown>
                <Dropdown.Toggle
                    size="sm"
                    variant="link"
                    className="p-0 object-pay-status-toggle"
                    disabled={disabled}
                >
                    {statusBadge}
                </Dropdown.Toggle>
                {typeof document !== 'undefined' ? createPortal(menu, document.body) : menu}
            </Dropdown>
        </div>
    );
};

export default ObjectPayStatus;
